
import { useMppContext } from './MppContextEnhanced'; // Use custom hook
import React, { useState, useCallback } from 'react';
import {
  useLazyGetApiMasterScheduleByScheduleIdTasksQuery,
  useLazyGetApiMasterScheduleByScheduleIdMilestonesQuery,
} from '../../../app/athenaApiGen';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Flag as FlagIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { StyledDataGridPro, StyledDataGrid } from '../../../shared-theme/StyledComponents';
import { GridFooterContainer, GridPagination } from '@mui/x-data-grid-pro';
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '../../../utils/tableVariables';

import '../css/styles.css';

// Business rule: levels 1 and 2 (depth 0 and 1) are expanded by default,
// level 3 and deeper (depth 2+) are collapsed by default. To realize that as
// a set of currently-collapsed parent task IDs, we collapse every task at
// depth >= 1 that itself has children — that hides its depth-2+ descendants
// while leaving depth-0 and depth-1 rows visible. User clicks on the +/-
// toggles override this initial state and persist for the session.
const computeDefaultCollapsedTaskIds = (tasks) => {
  if (!Array.isArray(tasks) || tasks.length === 0) return new Set();
  const taskById = new Map(tasks.map((t) => [t.id, t]));
  const depthById = new Map();
  const getDepth = (task, seen = new Set()) => {
    if (depthById.has(task.id)) return depthById.get(task.id);
    if (!task.parentTaskId || task.parentTaskId === 0 || seen.has(task.id)) {
      depthById.set(task.id, 0);
      return 0;
    }
    seen.add(task.id);
    const parent = taskById.get(task.parentTaskId);
    const depth = parent ? getDepth(parent, seen) + 1 : 0;
    depthById.set(task.id, depth);
    return depth;
  };
  for (const t of tasks) getDepth(t);
  const hasChildren = new Set();
  for (const t of tasks) {
    if (t.parentTaskId && t.parentTaskId !== 0) hasChildren.add(t.parentTaskId);
  }
  const collapsed = new Set();
  for (const t of tasks) {
    if ((depthById.get(t.id) ?? 0) >= 1 && hasChildren.has(t.id)) {
      collapsed.add(t.id);
    }
  }
  return collapsed;
};

const CustomFooter = () => {
  return (
    <GridFooterContainer sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
      <GridPagination />
    </GridFooterContainer>
  );
};

const MasterScheduleTable = () => {
    // Get context data - this is now our single source of truth
    const { data, loading, error, uploadInProgress } = useMppContext();

    // Component-specific state only
    const [scheduleTasks, setScheduleTasks] = useState({});
    const [scheduleMilestones, setScheduleMilestones] = useState({});
    const [expandedSchedules, setExpandedSchedules] = useState({});
    // Per-schedule set of task IDs whose descendants are currently hidden.
    // Shape: { [scheduleId]: Set<taskId> }. Default empty -> everything expanded.
    const [collapsedTasksBySchedule, setCollapsedTasksBySchedule] = useState({});
    // Filter-bar state. Global (not per-schedule) so the preference carries
    // across schedule rows the user expands in the same session. Default
    // unchecked => completed items are hidden until the user opts in.
    // Kept independent for tasks vs. milestones so the user can see completed
    // milestones while still hiding completed tasks (or vice versa).
    // One-of selector for the Task View's completed-visibility filter.
    // '' = no completion radio chosen (default: hide completed). Selecting
    // a radio narrows; the "Clear" button resets back to empty.
    const [taskFilter, setTaskFilter] = useState('');
    // "Expand All Tasks" toggle. When on: completed tasks are also
    // surfaced (in addition to the checkbox clearing the collapsed set
    // on transition). Tracked separately from the collapsed set so that
    // a user collapsing one row via +/- doesn't flip this off and
    // suddenly re-hide every completed item.
    const [showAllTasks, setShowAllTasks] = useState(false);
    // One-of selector for the Milestone View. Empty string = no radio
    // chosen (default: show non-completed milestones). Selecting a radio
    // narrows the view; the "Clear" button resets back to empty.
    const [milestoneFilter, setMilestoneFilter] = useState('');
    const [taskSearchQuery, setTaskSearchQuery] = useState('');
    const [milestoneSearchQuery, setMilestoneSearchQuery] = useState('');
    const [fetchTasks] = useLazyGetApiMasterScheduleByScheduleIdTasksQuery();
    const [fetchMilestones] = useLazyGetApiMasterScheduleByScheduleIdMilestonesQuery();

  const toggleScheduleExpansion = useCallback(async (id) => {
    const isExpanding = !expandedSchedules[id];

    setExpandedSchedules(prev => ({
      ...prev,
      [id]: isExpanding
    }));

    // Fetch tasks and milestones when expanding
    if (isExpanding && !scheduleTasks[id]) {
      try {
        const [tasksResult, milestonesResult] = await Promise.all([
          fetchTasks({ scheduleId: id }).unwrap(),
          fetchMilestones({ scheduleId: id }).unwrap(),
        ]);

        setScheduleTasks(prev => ({ ...prev, [id]: tasksResult }));
        setScheduleMilestones(prev => ({ ...prev, [id]: milestonesResult }));
        // Seed the default expand/collapse state for this schedule's tasks.
        // Only seed if we haven't already — preserves any clicks the user
        // made during a previous expand of this same schedule.
        // Seed default expand/collapse using BOTH tasks and milestones —
        // milestones are rendered inline in the merged Task View, so their
        // hierarchy contributes to which parents get auto-collapsed.
        setCollapsedTasksBySchedule((prev) =>
          prev[id] !== undefined
            ? prev
            : {
                ...prev,
                [id]: computeDefaultCollapsedTaskIds([
                  ...(Array.isArray(tasksResult) ? tasksResult : []),
                  ...(Array.isArray(milestonesResult) ? milestonesResult : []),
                ]),
              },
        );
      } catch (err) {
        console.error('Error fetching schedule details:', err);
      }
    }
  }, [scheduleTasks, fetchTasks, fetchMilestones, expandedSchedules]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Helper to extract outline number and clean task name
  const parseTaskName = (name) => {
    if (!name || typeof name !== 'string') return { outlineNumber: '', cleanName: name || '' };
    // Match leading digits (outline number) followed by the rest of the name
    const match = name.match(/^(\d+)\s*(.+)$/);
    if (match) {
      return { outlineNumber: match[1], cleanName: match[2].trim() };
    }
    // If only digits or no match, return as-is
    return { outlineNumber: '', cleanName: name };
  };

  // Task columns for nested DataGrid
  const taskColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
    },
    {
      field: 'name',
      headerName: 'Task Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const { cleanName } = parseTaskName(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {params.row.parentTaskId != null && params.row.parentTaskId !== 0 && (
              <Box sx={{ width: 20, height: 1, bgcolor: 'grey.400', mr: 1 }} />
            )}
            <Typography variant="body2">
              {cleanName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 110,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'finishDate',
      headerName: 'End Date',
      width: 110,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'durationDays',
      headerName: 'Duration',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}d</Typography>
      ),
    },
    {
      field: 'percentComplete',
      headerName: 'Progress',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}%</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const isCompleted = params.value === 'Completed';
        return (
          <Chip
            icon={isCompleted ? <CheckCircleIcon /> : <AccessTimeIcon />}
            label={params.value}
            size="small"
            sx={{
              backgroundColor: isCompleted ? '#e8f5e9' : '#f5f5f5',
              color: isCompleted ? '#2e7d32' : '#757575',
              '& .MuiChip-icon': {
                color: isCompleted ? '#2e7d32' : '#757575',
              },
            }}
          />
        );
      },
    },
    {
      field: 'resourceNames',
      headerName: 'Resources',
      width: 100,
      renderCell: (params) => params.value ? (
        <Typography variant="body2">{params.value?.split(',').length}</Typography>
      ) : null,
    },
    {
      field: 'critical',
      headerName: 'Critical',
      width: 80,
      renderCell: (params) => params.value ? (
        <Tooltip title="Critical Path">
          <FlagIcon color="error" />
        </Tooltip>
      ) : null,
    },
  ];

  // Milestone columns for the standalone Milestones summary table. Renders
  // milestones in isolation (no hierarchy), independent of the merged
  // Task View — useful for at-a-glance milestone tracking.
  const milestoneColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
    },
    {
      field: 'name',
      headerName: 'Milestone Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: 'startDate',
      headerName: 'Date',
      width: 120,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const status = params.value;
        const isCompleted = status === 'Completed';
        // Compute "late" and "due soon" inline so the chip reflects the
        // same date-derived signals the Late Only / Due ≤ 14d radio
        // filters use. Completed milestones are never late/due — they're
        // done — so the completed chip wins when both could apply.
        let derived = null;
        if (!isCompleted) {
          const dateStr = params.row.finishDate || params.row.startDate;
          const mDate = dateStr ? new Date(dateStr) : null;
          if (mDate && !isNaN(mDate)) {
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const endOfDueWindow = new Date(startOfToday);
            endOfDueWindow.setDate(endOfDueWindow.getDate() + 14);
            endOfDueWindow.setHours(23, 59, 59, 999);
            if (mDate < startOfToday) {
              derived = 'late';
            } else if (mDate <= endOfDueWindow) {
              derived = 'dueSoon';
            }
          }
        }
        if (derived === 'late') {
          return (
            <Chip
              label="Late"
              size="small"
              sx={{
                backgroundColor: '#fdecea',
                color: '#c62828',
                fontWeight: 600,
              }}
            />
          );
        }
        if (derived === 'dueSoon') {
          return (
            <Chip
              label="Due Soon"
              size="small"
              sx={{
                backgroundColor: '#fff4e5',
                color: '#ef6c00',
                fontWeight: 600,
              }}
            />
          );
        }
        return (
          <Chip
            icon={isCompleted ? <CheckCircleIcon /> : <AccessTimeIcon />}
            label={status}
            size="small"
            sx={{
              backgroundColor: isCompleted ? '#e8f5e9' : '#f5f5f5',
              color: isCompleted ? '#2e7d32' : '#757575',
              '& .MuiChip-icon': {
                color: isCompleted ? '#2e7d32' : '#757575',
              },
            }}
          />
        );
      },
    },
    {
      field: 'critical',
      headerName: 'Critical',
      width: 80,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title="Critical Milestone">
            <FlagIcon color="error" />
          </Tooltip>
        ) : null,
    },
  ];

  // Column definitions for the DataGrid
  const columns = [
    {
      field: 'rowNumber',
      headerName: '#',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.api.getAllRowIds().indexOf(params.row.id) + 1;
      },
    },
    {
      field: 'name',
      headerName: 'Schedule Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'filename',
      headerName: 'File Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => row.filename || '-',
    },
    {
      field: 'duration',
      headerName: 'Schedule Duration',
      width: 200,
      valueGetter: (value, row) => {
        const start = row.projectData?.startDate;
        const finish = row.projectData?.finishDate;
        return start && finish ? `${formatDate(start)} - ${formatDate(finish)}` : 'N/A';
      },
    },
    {
      field: 'uploadedAt',
      headerName: 'Uploaded At',
      width: 160,
      valueGetter: (value, row) => (row.uploadedAt ? formatDate(row.uploadedAt) : '-'),
    },
  ];

  // Detail panel content renderer
  const getDetailPanelContent = useCallback(
    ({ row }) => {
      const tasks = scheduleTasks[row.id] || [];
      const milestones = scheduleMilestones[row.id] || [];

      // Merge tasks + milestones into a single unified list. MS Project
      // treats a milestone as a zero-duration task, so we render them in
      // the same outline view distinguished only by a diamond marker.
      // Deduplicate by id in case any milestone also appeared in /tasks.
      const allItems = [];
      const seenIds = new Set();
      const addItem = (item) => {
        if (item?.id == null || seenIds.has(item.id)) return;
        seenIds.add(item.id);
        allItems.push(item);
      };
      if (Array.isArray(tasks)) tasks.forEach(addItem);
      if (Array.isArray(milestones)) milestones.forEach(addItem);

      // Build per-schedule helpers for the +/- expand-collapse column.
      const scheduleId = row.id;
      const collapsedIds = collapsedTasksBySchedule[scheduleId] || new Set();
      const taskById = new Map(allItems.map((t) => [t.id, t]));
      const taskHasChildren = new Set();
      for (const t of allItems) {
        if (t.parentTaskId && t.parentTaskId !== 0) {
          taskHasChildren.add(t.parentTaskId);
        }
      }
      // Precompute hierarchy depth per task (0 = root) so the Task Name
      // column can indent proportionally instead of a single 20px tick for
      // every non-root row.
      const taskDepthById = new Map();
      const computeTaskDepth = (task, seen = new Set()) => {
        if (taskDepthById.has(task.id)) return taskDepthById.get(task.id);
        if (!task.parentTaskId || task.parentTaskId === 0 || seen.has(task.id)) {
          taskDepthById.set(task.id, 0);
          return 0;
        }
        seen.add(task.id);
        const parent = taskById.get(task.parentTaskId);
        const depth = parent ? computeTaskDepth(parent, seen) + 1 : 0;
        taskDepthById.set(task.id, depth);
        return depth;
      };
      for (const t of allItems) computeTaskDepth(t);
      // A task is hidden iff any ancestor in its parent chain is currently
      // collapsed. `seen` guards against malformed cycles in upstream MPP data.
      const isAncestorCollapsed = (task) => {
        const seen = new Set();
        let parentId = task.parentTaskId;
        while (parentId && parentId !== 0 && !seen.has(parentId)) {
          seen.add(parentId);
          if (collapsedIds.has(parentId)) return true;
          parentId = taskById.get(parentId)?.parentTaskId;
        }
        return false;
      };
      const searchNeedle = taskSearchQuery.trim().toLowerCase();
      // 7-day cutoff for the "Show Completed within 7d" filter. Completed
      // tasks whose finishDate falls within this window count as "recent".
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      // Today at 00:00 — used by the "Show Late Only" milestone filter so
      // a milestone whose planned date is *today* doesn't count as late
      // (still some hours left in the day to complete it).
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      // 14-day window from today for the "Due in 14d" milestone filter.
      const endOfDueWindow = new Date(startOfToday);
      endOfDueWindow.setDate(endOfDueWindow.getDate() + 14);
      endOfDueWindow.setHours(23, 59, 59, 999);

      // Precompute per-filter match counts so each checkbox can show how
      // many rows it would surface. Counts are independent of each other —
      // they reflect what the filter would match on its own, not the
      // intersection with other active filters.
      const parseMilestoneDate = (m) => {
        const dateStr = m.finishDate || m.startDate;
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return isNaN(d) ? null : d;
      };
      const safeMilestones = Array.isArray(milestones) ? milestones : [];
      const lateMilestonesCount = safeMilestones.filter((m) => {
        if (m.status === 'Completed') return false;
        const d = parseMilestoneDate(m);
        return d && d < startOfToday;
      }).length;
      const dueIn14dMilestonesCount = safeMilestones.filter((m) => {
        if (m.status === 'Completed') return false;
        const d = parseMilestoneDate(m);
        return d && d >= startOfToday && d <= endOfDueWindow;
      }).length;
      const recentCompletedMilestonesCount = safeMilestones.filter((m) => {
        if (m.status !== 'Completed' || !m.finishDate) return false;
        const f = new Date(m.finishDate);
        return !isNaN(f) && f >= sevenDaysAgo;
      }).length;
      const allCompletedMilestonesCount = safeMilestones.filter(
        (m) => m.status === 'Completed',
      ).length;
      const recentCompletedTasksCount = allItems.filter((t) => {
        if (t.status !== 'Completed' || !t.finishDate) return false;
        const f = new Date(t.finishDate);
        return !isNaN(f) && f >= sevenDaysAgo;
      }).length;
      const allCompletedTasksCount = allItems.filter(
        (t) => t.status === 'Completed',
      ).length;
      const visibleTasks = allItems.filter((t) => {
            // Search bypasses the collapsed-ancestor filter so matches
            // deep inside collapsed parents still surface. Matches against
            // task name OR task id (so users can search by either).
            if (searchNeedle) {
              const nameMatch =
                t.name && t.name.toLowerCase().includes(searchNeedle);
              const idMatch =
                t.id != null && String(t.id).includes(searchNeedle);
              if (!nameMatch && !idMatch) return false;
            } else if (!taskFilter && isAncestorCollapsed(t)) {
              // Selecting a radio is an explicit "show me this view" action,
              // so it should override any collapsed-ancestor state — every
              // row in the radio's count appears in the table.
              return false;
            }
            // Exclusive radio-style completion filter. '' = default (hide
            // completed). Otherwise narrow to the selected subset.
            switch (taskFilter) {
              case 'recent7d':
                if (t.status !== 'Completed' || !t.finishDate) return false;
                {
                  const finish = new Date(t.finishDate);
                  return !isNaN(finish) && finish >= sevenDaysAgo;
                }
              case 'allCompleted':
                return t.status === 'Completed';
              default:
                // No radio selected: hide completed by default, unless
                // the user explicitly opted in via "Expand All Tasks".
                return showAllTasks || t.status !== 'Completed';
            }
          });
      const toggleTaskCollapsed = (taskId) => {
        setCollapsedTasksBySchedule((prev) => {
          const nextSet = new Set(prev[scheduleId] || []);
          if (nextSet.has(taskId)) nextSet.delete(taskId);
          else nextSet.add(taskId);
          return { ...prev, [scheduleId]: nextSet };
        });
      };

      // Splice the +/- toggle column between ID (idx 0) and Task Name (idx 1).
      // Leaf tasks render an empty cell so only parents show a control.
      const expandColumn = {
        field: '__expandToggle',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (!taskHasChildren.has(params.row.id)) return null;
          const isCollapsed = collapsedIds.has(params.row.id);
          return (
            <IconButton
              size="small"
              aria-label={isCollapsed ? 'Expand subtasks' : 'Collapse subtasks'}
              onClick={(e) => {
                e.stopPropagation();
                toggleTaskCollapsed(params.row.id);
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 14,
                  border: '1px solid',
                  borderColor: 'grey.400',
                  color: 'grey.800',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                {isCollapsed ? '+' : '−'}
              </Box>
            </IconButton>
          );
        },
      };
      // Depth-aware override of the Task Name column: walks each row's
      // parent chain to compute its outline level, then indents the
      // existing tick + label by (depth - 1) * 20px so depth 2 sits a
      // step further right than depth 1, and so on.
      const nameColumnIdx = taskColumns.findIndex((c) => c.field === 'name');
      const nameColumnWithIndent = {
        ...taskColumns[nameColumnIdx],
        renderCell: (params) => {
          const { cleanName } = parseTaskName(params.value);
          const depth = taskDepthById.get(params.row.id) || 0;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: depth > 0 ? `${(depth - 1) * 20}px` : 0,
                width: '100%',
                minWidth: 0,
              }}
            >
              {depth > 0 && (
                <Box
                  sx={{
                    width: 20,
                    height: 1,
                    bgcolor: 'grey.400',
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                variant="body2"
                noWrap
                title={cleanName}
                sx={{
                  fontWeight: depth === 0 ? 700 : 400,
                  minWidth: 0,
                }}
              >
                {cleanName}
              </Typography>
            </Box>
          );
        },
      };
      const taskColumnsWithToggle = [
        taskColumns[0],
        expandColumn,
        nameColumnWithIndent,
        ...taskColumns.slice(nameColumnIdx + 1),
      ];

      // Wait for both fetches to land before rendering either section.
      // Otherwise Tasks renders first (its accordion is unconditional) and
      // Milestones pops in above it once its fetch resolves, making the
      // Tasks block visibly jump down on initial expand.
      const detailsStillLoading =
        scheduleTasks[row.id] === undefined ||
        scheduleMilestones[row.id] === undefined;

      return (
        <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
          {detailsStillLoading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Loading schedule details...
              </Typography>
            </Box>
          )}
          {/* Milestone View — flat standalone summary of milestones */}
          {!detailsStillLoading && Array.isArray(milestones) && milestones.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Milestone View
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    m: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search by name or ID…"
                    value={milestoneSearchQuery}
                    onChange={(e) => setMilestoneSearchQuery(e.target.value)}
                    sx={{ width: 260 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Box
                    sx={(theme) => ({
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 0.5,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    })}
                  >
                    <RadioGroup
                      row
                      value={milestoneFilter}
                      onChange={(e) => setMilestoneFilter(e.target.value)}
                      sx={{ gap: 2, alignItems: 'center' }}
                    >
                      <FormControlLabel
                        value="late"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Late Only
                            </Box>{' '}
                            ({lateMilestonesCount})
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        value="due14d"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Due ≤ 14d
                            </Box>{' '}
                            ({dueIn14dMilestonesCount})
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        value="recent7d"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Completed ≤ 7d
                            </Box>{' '}
                            ({recentCompletedMilestonesCount})
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        value="allCompleted"
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              All Completed
                            </Box>{' '}
                            ({allCompletedMilestonesCount})
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </RadioGroup>
                    <Button
                      size="small"
                      onClick={() => setMilestoneFilter('')}
                      disabled={!milestoneFilter}
                      sx={{
                        ml: 2,
                        textTransform: 'none',
                        minWidth: 0,
                        py: 0,
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
                <StyledDataGrid
                  rows={milestones.filter((m) => {
                    // Name/ID search — same shape as Task View's filter.
                    const needle = milestoneSearchQuery.trim().toLowerCase();
                    if (needle) {
                      const nameMatch =
                        m.name && m.name.toLowerCase().includes(needle);
                      const idMatch =
                        m.id != null && String(m.id).includes(needle);
                      if (!nameMatch && !idMatch) return false;
                    }
                    // Exclusive radio-style filter — exactly one lens at
                    // a time. 'active' = default (non-completed). Others
                    // narrow to a specific subset; 0 matches -> empty.
                    const mDate = (() => {
                      const dateStr = m.finishDate || m.startDate;
                      if (!dateStr) return null;
                      const d = new Date(dateStr);
                      return isNaN(d) ? null : d;
                    })();
                    switch (milestoneFilter) {
                      case 'late':
                        return (
                          m.status !== 'Completed' &&
                          mDate &&
                          mDate < startOfToday
                        );
                      case 'due14d':
                        return (
                          m.status !== 'Completed' &&
                          mDate &&
                          mDate >= startOfToday &&
                          mDate <= endOfDueWindow
                        );
                      case 'recent7d':
                        if (m.status !== 'Completed' || !m.finishDate)
                          return false;
                        {
                          const finish = new Date(m.finishDate);
                          return !isNaN(finish) && finish >= sevenDaysAgo;
                        }
                      case 'allCompleted':
                        return m.status === 'Completed';
                      default:
                        // No radio selected: show non-completed milestones.
                        return m.status !== 'Completed';
                    }
                  })}
                  columns={milestoneColumns}
                  getRowId={(milestone) =>
                    milestone.id || `milestone-${row.id}-${milestone.name}`
                  }
                  autoHeight
                  pagination
                  pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
                  initialState={{
                    pagination: { paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE } },
                    sorting: { sortModel: [{ field: 'startDate', sort: 'asc' }] },
                  }}
                  slotProps={{
                    basePagination: {
                      showFirstButton: true,
                      showLastButton: true,
                    },
                  }}
                  localeText={{ noRowsLabel: 'No results found.' }}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  density="compact"
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Task View — merged tasks + milestones, hierarchy preserved */}
          {!detailsStillLoading && (
            <Accordion
              sx={
                Array.isArray(milestones) && milestones.length > 0
                  ? { mt: 2 }
                  : undefined
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Task View
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {allItems.length > 0 ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        m: 1,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <TextField
                          size="small"
                          placeholder="Search by name or ID…"
                          value={taskSearchQuery}
                          onChange={(e) => setTaskSearchQuery(e.target.value)}
                          sx={{ width: 260 }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                        <Box
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                          })}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={showAllTasks}
                                onChange={(e) => {
                                  const next = e.target.checked;
                                  setShowAllTasks(next);
                                  if (next) {
                                    // Expand all: clear the collapsed set
                                    // so every parent's children are open.
                                    setCollapsedTasksBySchedule((prev) => ({
                                      ...prev,
                                      [scheduleId]: new Set(),
                                    }));
                                  } else {
                                    // Re-seed with the level-3+ default so
                                    // unchecking returns to the original
                                    // outline-friendly state.
                                    setCollapsedTasksBySchedule((prev) => ({
                                      ...prev,
                                      [scheduleId]:
                                        computeDefaultCollapsedTaskIds(allItems),
                                    }));
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  Expand All Tasks
                                </Box>
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={(theme) => ({
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.5,
                          py: 0.5,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                        })}
                      >
                        <RadioGroup
                          row
                          value={taskFilter}
                          onChange={(e) => setTaskFilter(e.target.value)}
                          sx={{ gap: 2, alignItems: 'center' }}
                        >
                          <FormControlLabel
                            value="recent7d"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  Completed ≤ 7d
                                </Box>{' '}
                                ({recentCompletedTasksCount})
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <FormControlLabel
                            value="allCompleted"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  All Completed
                                </Box>{' '}
                                ({allCompletedTasksCount})
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </RadioGroup>
                        <Button
                          size="small"
                          onClick={() => setTaskFilter('')}
                          disabled={!taskFilter}
                          sx={{
                            ml: 2,
                            textTransform: 'none',
                            minWidth: 0,
                            py: 0,
                          }}
                        >
                          Clear
                        </Button>
                      </Box>
                    </Box>
                    <StyledDataGrid
                      rows={visibleTasks}
                      columns={taskColumnsWithToggle}
                    getRowId={(task) => task.id || `task-${row.id}-${task.name}`}
                    autoHeight
                    pagination
                    pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
                    initialState={{
                      pagination: { paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE } },
                      sorting: { sortModel: [{ field: 'id', sort: 'asc' }] },
                    }}
                    slotProps={{
                      basePagination: {
                        showFirstButton: true,
                        showLastButton: true,
                      },
                    }}
                    localeText={{ noRowsLabel: 'No results found.' }}
                    disableRowSelectionOnClick
                    density="compact"
                  />
                  </>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks available
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

        </Box>
      );
    },
    [
      scheduleTasks,
      scheduleMilestones,
      collapsedTasksBySchedule,
      taskFilter,
      showAllTasks,
      milestoneFilter,
      taskSearchQuery,
      milestoneSearchQuery,
      taskColumns,
      milestoneColumns,
    ]
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading schedule data...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Error loading schedule data: {error.message}
        </Typography>
      </Box>
    );
  }

  // Show no data state
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No Master Schedule Data Available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upload a .mpp file to see schedule data here.
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ width: '100%' }}>
      {/* Show refresh indicator during upload */}
      {uploadInProgress && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="primary">
            File upload in progress... Data will refresh automatically.
          </Typography>
          <LinearProgress />
        </Box>
      )}

      <Paper sx={{ width: '100%' }}>
        <StyledDataGridPro
          rows={data || []}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pagination
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: { paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE } },
          }}
          autoHeight
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          onDetailPanelExpandedRowIdsChange={(newIds) => {
            // Fetch data for newly expanded rows
            newIds.forEach(id => toggleScheduleExpansion(id));
          }}
          slots={{
            footer: CustomFooter,
          }}
          sx={{
            '& .MuiDataGrid-detailPanel': {
              bgcolor: 'rgba(0, 0, 0, 0.02)',
            },
            '& .MuiDataGrid-detailPanelToggleCell .MuiDataGrid-cell--textLeft': {
              overflow: 'hidden',
            },
            '& .MuiDataGrid-detailPanelToggleCell': {
              minWidth: '40px !important',
              maxWidth: '40px !important',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default MasterScheduleTable;
